import './DetailsFinancialBenefits.scss';
import { Card, Typography, Loader, LabelValue, Checkbox, Dropdown, Input, Icon, Button } from '@design-system/index';
import '../../DetailsPage.scss';
import { Grid } from '@design-system/components/Grid/Grid';
import { formatDate } from '@utils/dates';
import { Table } from '@components/Table/Table/Table';
import { ColumnDef } from '@tanstack/react-table';
import {
    FinancialBenefitSchedule,
    Member,
    FinancialBenefitToCreate,
    FinancialBenefitScheduleToUpdate
} from '@services/member/member/member.types';
import { FinancialBenefitListItem } from '@services/member/list/financial-benefits/financial-benefits.types';
import moment from 'moment';
import { formatMoney } from '@utils/numbers';
import { useDetailsFinancialBenefitsVM } from './useDetailsFinancialBenefitsVM';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import { Formik } from 'formik';
import { FinancialBenefit } from '@services/member/member/member.types';
import { ArchiveFilled, Checkmark, ModifierLine, ModifierFull, UnarchiveFilled, UnarchiveLine, ArchiveLine } from '@icons';

export default function DetailsFinancialBenefits() {
    const {
        isLoading,
        isError,
        member,
        financialBenefitScheduleColumnHelper,
        financialBenefitColumnHelper,
        isArchived,
        mutationHook,
        handleToggleArchiveFinancialBenefit,
        handleToggleArchiveFinancialBenefits,
        id,
        needReload,
        handleMetadata,
        setIsArchived,
        MySwal,
        addMemberFinancialBenefit,
        selectedFinancialBenefit,
        setSelectedFinancialBenefit,
        handleUpdateFinancialBenefitSchedule,
        financialBenefits,
        handleUpdateFinancialBenefitComplete
    } = useDetailsFinancialBenefitsVM();

    const handleUpdateFinancialBenefitScheduleModal = async (financialBenefitSchedule: FinancialBenefitSchedule) => {
        await MySwal.fire<FinancialBenefitToCreate>({
            title: "Modification d'une échéance",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Modifier',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <div className="card swal-formik-card">
                    <Formik
                        initialValues={{
                            id: financialBenefitSchedule.id,
                            scheduledAt: financialBenefitSchedule.scheduledAt,
                            pointedAt: financialBenefitSchedule.pointedAt,
                            isPointed: financialBenefitSchedule.isPointed,
                            yearAt: moment(financialBenefitSchedule.scheduledAt).year(),
                            monthAt: moment(financialBenefitSchedule.scheduledAt).month()
                        }}
                        onSubmit={async (values: FinancialBenefitScheduleToUpdate) => {
                            values.scheduledAt = moment(`${values.yearAt}-${String(values.monthAt + 1).padStart(2, '0')}-01`).format(
                                'YYYY-MM-DD'
                            );
                            await handleUpdateFinancialBenefitSchedule(values);
                            MySwal.close();
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue }) => (
                            <form id="form-update-member-financial-benefit-schedule" className="form" onSubmit={handleSubmit}>
                                <div className="form-section-container">
                                    <div className="form-section">
                                        <div className="form-section-fields">
                                            <div className="form-line">
                                                <div className="form-group">
                                                    <Input
                                                        type="number"
                                                        name="scheduledAt"
                                                        label="Année de l'échéance"
                                                        placeholder="Année de l'échéance"
                                                        required
                                                        value={moment(values.scheduledAt).format('YYYY')}
                                                        onChange={(e) => {
                                                            setFieldValue('yearAt', moment(e.target.value).year());
                                                        }}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <Dropdown
                                                        name="monthAt"
                                                        label="Mois de l'échéance"
                                                        value={values.monthAt}
                                                        required
                                                        onOptionChange={(option) => {
                                                            setFieldValue('monthAt', option.id);
                                                        }}
                                                        options={moment.months().map((month, index) => ({
                                                            label: month,
                                                            value: index,
                                                            id: index
                                                        }))}
                                                    />
                                                </div>
                                            </div>
                                            <div className="form-line">
                                                <div className="form-group">
                                                    <Checkbox
                                                        checked={values.isPointed}
                                                        onChange={() => {
                                                            setFieldValue('isPointed', !values.isPointed);
                                                        }}
                                                    >
                                                        Prélevée
                                                    </Checkbox>
                                                </div>
                                                {values.isPointed && (
                                                    <div className="form-group">
                                                        <Input
                                                            type="date"
                                                            name="pointedAt"
                                                            label="Prélevée le"
                                                            placeholder="Prélevée le"
                                                            required
                                                            value={values.pointedAt}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-update-member-financial-benefit-schedule') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const handleAddFinancialBenefit = async () => {
        await MySwal.fire<FinancialBenefitToCreate>({
            title: "Création d'une prestation financière",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <Formik
                    initialValues={{
                        template: '',
                        code: '',
                        amount: 0,
                        checkNumber: '',
                        additionalNumber: '',
                        total: 0,
                        schedulesCount: 0,
                        nextFinancialBenefitSchedules: [],
                        requestedAt: new Date().toISOString().split('T')[0],
                        scheduleAt: '',
                        financialBenefitSchedules: [],
                        member: member as Member
                    }}
                    onSubmit={(values) => {
                        addMemberFinancialBenefit({
                            id: String(id),
                            financialBenefit: {
                                scheduleAt: values.scheduleAt,
                                code: values.code,
                                amount: values.amount,
                                checkNumber: values.checkNumber,
                                additionalNumber: values.additionalNumber,
                                requestedAt: values.requestedAt,
                                schedulesCount: values.schedulesCount,
                                financialBenefitSchedules: values.financialBenefitSchedules,
                                total: values.total,
                                nextFinancialBenefitSchedules: values.nextFinancialBenefitSchedules,
                                member: values.member,
                                isArchived: false
                            }
                        });
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit, setFieldValue }) => (
                        <form id="form-add-member-financial-benefit" onSubmit={handleSubmit}>
                            <Dropdown
                                name="template"
                                label="Code"
                                required
                                value={values.template}
                                onOptionChange={(option) => {
                                    const selected = financialBenefits.find(
                                        (financialBenefit: FinancialBenefitListItem) => financialBenefit.code === option.label
                                    );
                                    setFieldValue('code', selected?.code || '');
                                }}
                                options={financialBenefits.map((financialBenefit: FinancialBenefitListItem) => ({
                                    label: financialBenefit.code,
                                    value: financialBenefit.code,
                                    id: financialBenefit.id
                                }))}
                            />
                            <Input
                                type="number"
                                name="amount"
                                label="Montant"
                                required
                                placeholder="Montant"
                                rightContent={'€'}
                                value={values.amount}
                                onChange={handleChange}
                            />
                            <Input
                                type="date"
                                name="requestedAt"
                                label="Date de demande"
                                placeholder="Date de demande"
                                required
                                value={values.requestedAt}
                                onChange={handleChange}
                            />
                            <Input
                                type="date"
                                name="scheduleAt"
                                label="Date de première échéance"
                                placeholder="Date de première échéance"
                                required
                                value={values.scheduleAt}
                                onChange={handleChange}
                            />
                            <Input
                                type="number"
                                name="schedulesCount"
                                label="Nombre d'échéances"
                                placeholder="Nombre d'échéances"
                                required
                                value={values.schedulesCount}
                                onChange={handleChange}
                            />
                            <Input
                                name="checkNumber"
                                label="Numéro de chèque"
                                placeholder="Numéro de chèque"
                                value={values.checkNumber}
                                onChange={handleChange}
                            />
                            <Input
                                name="additionalNumber"
                                label="Numéro"
                                placeholder="Numéro"
                                value={values.additionalNumber}
                                onChange={handleChange}
                            />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-add-member-financial-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const handleUpdateFinancialBenefitModal = async (financialBenefit: FinancialBenefit) => {
        await MySwal.fire({
            title: "Modification d'une prestation financière",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Modifier',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            heightAuto: false,
            html: (
                <div className="card swal-formik-card">
                    <Formik
                        initialValues={{
                            template: financialBenefits.find((fb) => fb.code === financialBenefit.code)?.id || '',
                            code: financialBenefit.code,
                            amount: financialBenefit.amount,
                            checkNumber: financialBenefit.checkNumber || '',
                            additionalNumber: financialBenefit.additionalNumber || '',
                            requestedAt: financialBenefit.requestedAt
                        }}
                        onSubmit={async (values) => {
                            await handleUpdateFinancialBenefitComplete(financialBenefit, values);
                            MySwal.close();
                        }}
                    >
                        {({ values, handleChange, handleSubmit, setFieldValue }) => (
                            <form id="form-update-financial-benefit" className="form" onSubmit={handleSubmit}>
                                <div className="form-section-container">
                                    <div className="form-section">
                                        <div className="form-section-fields">
                                            <Dropdown
                                                name="template"
                                                label="Code"
                                                required
                                                value={values.template}
                                                onOptionChange={(option) => {
                                                    const selected = financialBenefits.find(
                                                        (fb: FinancialBenefitListItem) => fb.id === option.id
                                                    );
                                                    setFieldValue('code', selected?.code || '');
                                                    setFieldValue('template', option.id);
                                                }}
                                                options={financialBenefits.map((fb: FinancialBenefitListItem) => ({
                                                    label: fb.code,
                                                    value: fb.code,
                                                    id: fb.id
                                                }))}
                                            />
                                            <Input
                                                type="number"
                                                name="amount"
                                                label="Montant"
                                                required
                                                placeholder="Montant"
                                                rightContent={'€'}
                                                value={values.amount}
                                                onChange={handleChange}
                                            />
                                            <Input
                                                type="date"
                                                name="requestedAt"
                                                label="Date de demande"
                                                placeholder="Date de demande"
                                                required
                                                value={values.requestedAt}
                                                onChange={handleChange}
                                            />
                                            <Input
                                                name="checkNumber"
                                                label="Numéro de chèque"
                                                placeholder="Numéro de chèque"
                                                value={values.checkNumber}
                                                onChange={handleChange}
                                            />
                                            <Input
                                                name="additionalNumber"
                                                label="Numéro"
                                                placeholder="Numéro"
                                                value={values.additionalNumber}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-update-financial-benefit') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const financialBenefitSchedulesColumns = [
        financialBenefitScheduleColumnHelper.accessor('scheduledAt', {
            id: 'year',
            header: 'Année',
            cell: (info) => <div className="schedule-date">{moment(info.getValue()).format('YYYY')}</div>,
            meta: {
                width: 'auto'
            }
        }),
        financialBenefitScheduleColumnHelper.accessor('scheduledAt', {
            id: 'month',
            header: 'Mois',
            cell: (info) => <div className="schedule-date">{moment(info.getValue()).format('MMMM')}</div>
        }),
        financialBenefitScheduleColumnHelper.accessor('total', {
            header: 'Montant',
            cell: (info) => (
                <div className={`schedule-amount ${info.row.original.isPointed ? 'pointed' : ''}`}>{formatMoney(info.getValue())}</div>
            )
        }),
        financialBenefitScheduleColumnHelper.accessor('pointedAt', {
            id: 'pointedAt',
            header: 'Prélevé le',
            cell: (info) => <>{info.getValue() ? moment(info.getValue()).format('DD/MM/YYYY') : ''}</>
        }),
        financialBenefitScheduleColumnHelper.display({
            header: 'Action',
            enableSorting: false,
            size: 0,
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleUpdateFinancialBenefitScheduleModal(info.row.original);
                            }}
                        />
                    </div>
                );
            }
        })
        /*financialBenefitScheduleColumnHelper.accessor('isPointed', {
            header: 'Pointage',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Checkbox checked={info.getValue()} onChange={() => handleCheckFinancialBenefitSchedule(info.row.original.id)} />
                </div>
            )
        })*/
    ] as ColumnDef<FinancialBenefitSchedule>[];

    const financialBenefitColumns = [
        financialBenefitColumnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            ),
            meta: {
                clickable: false
            }
        }),
        financialBenefitColumnHelper.display({
            id: 'displayed',
            header: 'Affichée ?',
            size: 0,
            meta: {
                style: {
                    textAlign: 'center'
                },
                clickable: true
            },
            cell: (info) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {selectedFinancialBenefit && selectedFinancialBenefit.id === info.row.original.id ? (
                        <Icon icon={<Checkmark />} color="primary" size={16} />
                    ) : null}
                </div>
            )
        }),
        financialBenefitColumnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => <>{info.getValue()}</>,
            meta: {
                clickable: true
            }
        }),
        financialBenefitColumnHelper.accessor('requestedAt', {
            header: 'Demandé le',
            enableSorting: true,
            cell: (info) => <>{formatDate(info.getValue())}</>,
            meta: {
                clickable: true
            }
        }),
        financialBenefitColumnHelper.accessor('amount', {
            header: 'Montant',
            enableSorting: true,
            cell: (info) => <>{formatMoney(info.getValue())}</>,
            meta: {
                clickable: true
            }
        }),
        financialBenefitColumnHelper.accessor('remainder', {
            header: 'Reste à rembourser',
            enableSorting: false,
            cell: (info) => <>{formatMoney(info.getValue())}</>,
            meta: {
                clickable: true
            }
        }),
        financialBenefitColumnHelper.display({
            id: 'update',
            size: 0,
            header: 'Modifier',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => handleUpdateFinancialBenefitModal(info.row.original)}
                        />
                    </div>
                );
            }
        }),
        financialBenefitColumnHelper.display({
            id: 'archive',
            size: 0,
            header: isArchived ? 'Désarchiver' : 'Archiver',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => handleToggleArchiveFinancialBenefit(info.row.original.id, !isArchived)}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<FinancialBenefit>[];

    if (isLoading) {
        return <Loader className="full-container-loader" />;
    }

    if (isError) {
        return (
            <Typography intrinsic="p" color="secondary">
                Une erreur est survenue lors de la récupération des informations de la fiche adhérent
            </Typography>
        );
    }

    if (!member) {
        return <>Une erreur est survenue lors de la récupération des informations de la fiche adhérent</>;
    }

    return (
        <div className="details-member-container">
            <div className="details-member-container-body">
                <Card headerType="very-light" headerTitle="Prestation financière sélectionnée" noBorder>
                    {selectedFinancialBenefit ? (
                        <Grid columns={2} className="details-member-financial-benefit-selected">
                            <Card headerType="very-light" showHeader={false}>
                                <Grid columns={3}>
                                    <LabelValue label="Code" value={selectedFinancialBenefit.code} />
                                    <LabelValue label="Demandé le" value={formatDate(selectedFinancialBenefit.requestedAt)} />
                                    {selectedFinancialBenefit.financialBenefitSchedules[0] && (
                                        <LabelValue
                                            label="Première échéance"
                                            value={formatDate(selectedFinancialBenefit.financialBenefitSchedules[0].scheduledAt)}
                                        />
                                    )}
                                    <LabelValue label="Montant du prêt" value={formatMoney(selectedFinancialBenefit.amount)} />
                                    <LabelValue label="Reste à rembourser" value={formatMoney(selectedFinancialBenefit.remainder)} />
                                    <LabelValue label="Nb d'échéances" value={selectedFinancialBenefit.financialBenefitSchedules.length} />
                                    {selectedFinancialBenefit.financialBenefitSchedules[0] && (
                                        <LabelValue
                                            label="Montant mensuel"
                                            value={formatMoney(selectedFinancialBenefit.financialBenefitSchedules[0].amount)}
                                        />
                                    )}
                                    <LabelValue label="Numéro de chèque" value={selectedFinancialBenefit.checkNumber} />
                                    <LabelValue label="Numéro" value={selectedFinancialBenefit.additionalNumber} />
                                </Grid>
                            </Card>
                            <Table data={selectedFinancialBenefit.financialBenefitSchedules} columns={financialBenefitSchedulesColumns} />
                        </Grid>
                    ) : (
                        <Typography intrinsic="p" color="secondary">
                            Aucune prestation financière en cours
                        </Typography>
                    )}
                </Card>
                <Card headerType="very-light" headerTitle="Prestations financières perçues">
                    <MutationTable
                        noHeader
                        key={isArchived ? 'archived' : 'active'}
                        columns={financialBenefitColumns}
                        mutationHook={mutationHook}
                        mutationHookParams={{ id: id }}
                        onRowClick={(row: FinancialBenefit) => setSelectedFinancialBenefit(row)}
                        defaultSorting={{
                            field: 'requestedAt',
                            direction: 'desc'
                        }}
                        className="no-card"
                        needReload={needReload}
                        deleting={{
                            enabled: isArchived,
                            entity: 'member_financial_benefit'
                        }}
                        createButtonOnClick={() => handleAddFinancialBenefit()}
                        createButtonLabel="Ajouter une prestation financière"
                        archiveButtonOnClick={() => setIsArchived(!isArchived)}
                        archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                        onMetadata={handleMetadata}
                        footer={{
                            actions: [
                                {
                                    label: isArchived ? 'Désarchiver' : 'Archiver',
                                    icon: <Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />,
                                    hoverIcon: (
                                        <Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />
                                    ),
                                    onClick: (lines: FinancialBenefit[]) => handleToggleArchiveFinancialBenefits(lines, !isArchived)
                                }
                            ]
                        }}
                    />
                </Card>
            </div>
        </div>
    );
}
